import { type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useRouter } from 'next/router';
import Link from 'next/link';

interface NavProps {
  section: {
    title: string;
    icon: LucideIcon;
  };
  links: Array<{
    id: string;
    title: string;
    icon: LucideIcon;
    path: string;
    disabled: boolean;
  }>;
}

export const SidebarNav = ({ links, section }: NavProps) => {
  const { pathname } = useRouter();

  return (
    <nav data-testid="sidebar-nav" className="flex flex-col space-y-px">
      {section && (
        <div className="flex max-h-12 min-h-12 items-center gap-x-2 border-t border-t-toolbar-bevel bg-ui-base p-0 px-3 text-neutral-500 dark:text-neutral-400">
          <section.icon className="icon-6" />
          <div className="line-clamp-1 text-left text-sm font-bold break-all uppercase">{section.title}</div>
        </div>
      )}

      {links && links.length > 0 && (
        <div className="space-y-px">
          {links?.map((link) => (
            <Link
              key={link.id}
              aria-disabled={link.disabled}
              tabIndex={link.disabled ? -1 : undefined}
              className={cn(
                'flex h-12 items-center gap-x-2 gap-y-1 py-3 pl-3 focus-visible:outline-none',
                link.path === pathname
                  ? 'bg-highlight-base hover:bg-highlight-hover-color  focus-visible:bg-highlight-hover-color'
                  : 'bg-ui-panel hover:bg-ui-base dark:hover:bg-ui-bevel focus-visible:bg-ui-base dark:focus-visible:bg-ui-bevel',
                link.disabled ? 'pointer-events-none opacity-50' : '',
              )}
              href={link.path}
            >
              <link.icon
                className={cn(
                  'h-6 min-h-6 w-6 min-w-6 text-[#999999]',
                  link.path === pathname && 'text-text-base dark:text-text-inverted',
                )}
              />
              <div
                className={cn(
                  'line-clamp-1 break-all pr-2 text-left',
                  link.path === pathname && 'text-text-base dark:text-text-inverted',
                )}
              >
                {link.title}
              </div>
            </Link>
          ))}
        </div>
      )}
    </nav>
  );
};
